import { Pagination, Table } from 'BreetComponents';
import { getQueryParamValue, type IPagination } from 'BreetHelpers';
import { useWindowSize } from 'BreetHooks';
import { useCallback, useEffect, useState } from 'react';

import { PaginationShort } from '../Pagination/PaginationShort';
import type { IDataTable } from './DataTable.helpers';
import { DataTableHeader } from './DataTableHeader';

const DataTable = <E extends { _id?: string } | { id?: string }>({
	isLoading,
	paginate,
	onPageChange,
	headerProps,
	className,
	data,
	isSearchFilter,
	onRefresh,
	tip,
	docItemsIdentifier,
	...rest
}: IDataTable<E>) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageLimit, setPageLimit] = useState(50);

	const windowSize = useWindowSize();

	const dataTableWrapperClassName = className ? `bt_table_data_item ${className}` : 'bt_table_data_item';

	useEffect(() => {
		if (!!paginate?.page && paginate.page !== currentPage) setCurrentPage(paginate.page);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginate?.page]);

	useEffect(() => {
		if (!!paginate?.page && paginate.page !== 1 && !data?.length) {
			const previousPageButton = document.getElementById('bt_pagination_prev');
			previousPageButton?.click();
		}
	}, [data?.length, paginate?.page]);

	useEffect(() => {
		const size = getQueryParamValue('size');

		if (size) setPageLimit(+size);
	}, []);

	const handlePageChange = useCallback(
		(paginateOptions: Partial<IPagination>) => {
			setCurrentPage(paginateOptions.currentPage ?? 1);
			setPageLimit(paginateOptions.pageLimit ?? pageLimit);
			if (onPageChange) onPageChange(paginateOptions);
		},
		[onPageChange, pageLimit]
	);

	return (
		<div className={dataTableWrapperClassName}>
			{headerProps && (
				<DataTableHeader
					onRefresh={onRefresh}
					{...headerProps}
					tip={tip}
				/>
			)}

			<Table
				{...rest}
				data={data}
				pageLimit={pageLimit}
				isLoading={isLoading}
				isSearchFilter={isSearchFilter}
			/>

			{!!data?.length && paginate ? (
				<>
					{windowSize.width >= 768 ? (
						<Pagination
							currentPage={currentPage}
							pageLimit={pageLimit}
							totalDocItems={paginate.totalDocs}
							onPageChange={handlePageChange}
							isLoading={isLoading}
							docItemsIdentifier={docItemsIdentifier}
						/>
					) : (
						<PaginationShort
							currentPage={currentPage}
							pageLimit={pageLimit}
							totalDocItems={paginate.totalDocs}
							onPageChange={handlePageChange}
							isLoading={isLoading}
						/>
					)}
				</>
			) : null}
		</div>
	);
};

export default DataTable;
